'use client';

import { c as _c } from "react/compiler-runtime";
import { useIsMutating } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { useCopyToClipboard } from 'usehooks-ts';
import { noteLinkCopied } from '@/components/install-prompts/install-prompt-store';
import { prV2UpdateStateMutationKey } from '@/data/v2/pr/mutation-keys';
import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { usePrV2UpdateState } from '@/data/v2/pr/use-pr-v2-update-state';
import { isPullRequestOpen, readHeadBranchName } from '@/lib/pr-v2/action-helpers';
import { useVercelPreviewStore } from '@/lib/stores/vercel-preview-store';
type UsePrV2PullRequestActionsOptions = {
  /**
   * Path (on the current origin) that "Copy link" and "Copy title as link" copy. Omitted, they
   * copy the current page URL, which on the PR page keeps its view.
   */
  linkPath?: string;
};

/**
 * Pull request actions shared by the actions menu and the PR page's keyboard shortcuts, so both
 * run the same callbacks.
 */
export function usePrV2PullRequestActions(owner, repo, number, t0) {
  const $ = _c(59);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    linkPath
  } = t1;
  const summaryQuery = usePrV2Summary(owner, repo, number);
  const updateStateMutation = usePrV2UpdateState(owner, repo, number);
  let t2;
  if ($[2] !== number || $[3] !== owner || $[4] !== repo) {
    t2 = prV2UpdateStateMutationKey(owner, repo, number);
    $[2] = number;
    $[3] = owner;
    $[4] = repo;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      mutationKey: t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const stateUpdatesInFlight = useIsMutating(t3);
  const [, copyToClipboard] = useCopyToClipboard();
  const pullRequest = summaryQuery.data?.pullRequest;
  let t4;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t4 = state => state.previewUrls[`${owner}/${repo}/${number}`] ?? null;
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const previewUrl = useVercelPreviewStore(t4);
  let t5;
  if ($[12] !== copyToClipboard) {
    t5 = async (value, successMessage) => {
      if (!value) {
        toast.error("Nothing to copy");
        return false;
      }
      const copied = await copyToClipboard(value);
      if (copied) {
        toast.success(successMessage);
        return true;
      }
      toast.error("Failed to copy");
      return false;
    };
    $[12] = copyToClipboard;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const copyText = t5;
  let t6;
  if ($[14] !== pullRequest?.url) {
    t6 = () => {
      const url = pullRequest?.url;
      if (!url) {
        toast.error("GitHub URL not available");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    };
    $[14] = pullRequest?.url;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  pullRequest?.url;
  const openOnGitHub = t6;
  let t7;
  if ($[16] !== previewUrl) {
    t7 = () => {
      if (!previewUrl) {
        toast.error("Preview deployment not available");
        return;
      }
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    };
    $[16] = previewUrl;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const openPreviewDeployment = t7;
  let t8;
  if ($[18] !== pullRequest || $[19] !== updateStateMutation) {
    t8 = () => {
      if (!pullRequest) {
        toast.error("Pull request is not loaded yet");
        return;
      }
      updateStateMutation.mutate({
        draft: !pullRequest.draft
      });
    };
    $[18] = pullRequest;
    $[19] = updateStateMutation;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const toggleDraft = t8;
  let t9;
  if ($[21] !== updateStateMutation) {
    t9 = state_0 => {
      updateStateMutation.mutate({
        state: state_0
      });
    };
    $[21] = updateStateMutation;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const setPullRequestState = t9;
  let t10;
  if ($[23] !== linkPath) {
    t10 = () => linkPath ? new URL(linkPath, window.location.origin).href : window.location.href;
    $[23] = linkPath;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const linkUrl = t10;
  let t11;
  if ($[25] !== copyText || $[26] !== linkUrl || $[27] !== pullRequest) {
    t11 = async () => {
      const copied_0 = await copyText(linkUrl(), "URL copied");
      if (copied_0 && pullRequest) {
        noteLinkCopied(pullRequest);
      }
    };
    $[25] = copyText;
    $[26] = linkUrl;
    $[27] = pullRequest;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const copyUrl = t11;
  let t12;
  if ($[29] !== copyText || $[30] !== number) {
    t12 = async () => {
      await copyText(number, "Pull request number copied");
    };
    $[29] = copyText;
    $[30] = number;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const copyPrNumber = t12;
  let t13;
  if ($[32] !== copyText || $[33] !== pullRequest?.metadata || $[34] !== pullRequest?.metadataSummary?.headRef) {
    t13 = async () => {
      const branchName = readHeadBranchName(pullRequest?.metadata, pullRequest?.metadataSummary?.headRef);
      await copyText(branchName ?? "", "Branch name copied");
    };
    $[32] = copyText;
    $[33] = pullRequest?.metadata;
    $[34] = pullRequest?.metadataSummary?.headRef;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  pullRequest?.metadata;
  pullRequest?.metadataSummary?.headRef;
  const copyBranchName = t13;
  let t14;
  if ($[36] !== copyText || $[37] !== pullRequest?.headSha) {
    t14 = async () => {
      await copyText(pullRequest?.headSha ?? "", "Branch hash copied");
    };
    $[36] = copyText;
    $[37] = pullRequest?.headSha;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  pullRequest?.headSha;
  const copyBranchHash = t14;
  let t15;
  if ($[39] !== copyText || $[40] !== linkUrl || $[41] !== pullRequest?.title) {
    t15 = async () => {
      const title = pullRequest?.title?.trim();
      if (!title) {
        toast.error("Pull request title not available");
        return;
      }
      await copyText(`[${title}](${linkUrl()})`, "Title copied as link");
    };
    $[39] = copyText;
    $[40] = linkUrl;
    $[41] = pullRequest?.title;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  pullRequest?.title;
  const copyTitleAsLink = t15;
  const t16 = pullRequest?.state;
  let t17;
  if ($[43] !== t16) {
    t17 = isPullRequestOpen(t16);
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const t18 = updateStateMutation.isPending || stateUpdatesInFlight > 0;
  let t19;
  if ($[45] !== copyBranchHash || $[46] !== copyBranchName || $[47] !== copyPrNumber || $[48] !== copyTitleAsLink || $[49] !== copyUrl || $[50] !== openOnGitHub || $[51] !== openPreviewDeployment || $[52] !== previewUrl || $[53] !== pullRequest || $[54] !== setPullRequestState || $[55] !== t17 || $[56] !== t18 || $[57] !== toggleDraft) {
    t19 = {
      pullRequest,
      previewUrl,
      isOpen: t17,
      isUpdatingState: t18,
      openOnGitHub,
      openPreviewDeployment,
      toggleDraft,
      setPullRequestState,
      copyUrl,
      copyPrNumber,
      copyBranchName,
      copyBranchHash,
      copyTitleAsLink
    };
    $[45] = copyBranchHash;
    $[46] = copyBranchName;
    $[47] = copyPrNumber;
    $[48] = copyTitleAsLink;
    $[49] = copyUrl;
    $[50] = openOnGitHub;
    $[51] = openPreviewDeployment;
    $[52] = previewUrl;
    $[53] = pullRequest;
    $[54] = setPullRequestState;
    $[55] = t17;
    $[56] = t18;
    $[57] = toggleDraft;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
}